<!--
  ReconCostPanel.vue
  콜봇 월 정산 · 대사 탭의 "vox 원가와 AI 시간 대사"(7차 개정에서 2-3 에서 옮김). 읽기 전용.

  요청: ~/md/front/admin-콜봇-월정산-프론트요청.md "화면 명세 2 › 대사 탭 › vox 원가와 AI 시간 대사",
        "요청 계약 2"(cost.split·cost.unit_rule), "요청 계약 7"(UNIT_RULE_MISMATCH — 비차단)
  - 고정 경고 박스: vox 대시보드 CSV 의 통화시간은 AI 시간이 아니다(AI 구간 + 상담사 연결 구간 × 2).
  - vox 통화 원가(cost.vox_call)와 그 분해(cost.split — vox 직접 전환료·통신료는 계약 추가 제안이라 있을 때만).
  - AI 응대 시간(usage.ai — 전화 통화, 수신 + 발신)과 올린 시간(10초 단위 수를 시간으로) × 원가 단가(rates.ai.cost_per_unit)를 vox 전화 AI
    원가(cost.split.voice_ai − usage.web_voice.vox_cost)와 견준다. split.voice_ai 에는 웹 음성 원가가 들어 있어 그대로
    견주면 차이가 웹 음성 원가만큼 벌어진다(C06·recon-diag-03). 옆의 통화 수도 같은 범위(10초 규칙의 대상 —
    settlementRules.aiTimeCalls)로 센다(recon-diag-06).
  - KPI "10초 규칙 일치"(cost.unit_rule): 일치 n / 대상 n, 기타 n(설명되는 차이 — 발신 통화의 차이 · 전환 통화의 초과분),
    불일치 n(수신 통화), 원가 없음 n(uncosted — 있을 때만, 네 수의 합 = 대상). 발신 통화는 원가가 다르면(발신 기본료·통신료)
    많든 적든 기타라 UNIT_RULE_MISMATCH 는 수신 통화만 잡는다(회신 "2026-10-19 개정" 2).
    아직 재계산 전이면(matched null) '—'. 불일치가 있으면 통화별 표(cost.unit_rule.mismatches),
    원가 없는 통화가 있으면 그 통화 ID(cost.unit_rule.uncosted_call_ids, 서버가 최대 20건 — 없는 옛 응답이면 건수만).
    불일치 표는 쪽으로 나누지 않고 모든 행을 보인다.
  - 이 칸들에 걸린 경고(UNIT_RULE_MISMATCH·UNIT_RULE_UNCOSTED·WEB_VOICE_COST_MISMATCH)는 코드·차단 여부·설명을 붙인다.
  데이터는 부모(ReconTab)가 넘기는 정산 상세 하나다. 따로 부르지 않는다.
-->
<template>
  <section class="cost-card" :aria-labelledby="titleId">
    <header class="cost-card__head">
      <h2 :id="titleId" class="cost-card__title">vox 원가와 AI 시간 대사</h2>
      <span class="cost-card__meta">vox 원가는 부가세 별도 · 회선 제외</span>
    </header>

    <div class="cost-alert" role="note">
      <i class="fa-solid fa-triangle-exclamation cost-alert__icon" aria-hidden="true"></i>
      <span>{{ CSV_DURATION_WARNING }}</span>
    </div>

    <div class="cost-tiles">
      <!-- vox 통화 원가와 분해 -->
      <div class="cost-tile">
        <span class="cost-tile__label">vox 통화 원가</span>
        <span class="cost-tile__value">{{ formatWon(voxCall) }}</span>
        <dl v-if="splitRows.length" class="cost-split">
          <div v-for="row in splitRows" :key="row.key" class="cost-split__row" :title="row.tip">
            <dt>{{ row.label }}</dt>
            <dd>{{ formatWon(row.value) }}</dd>
          </div>
        </dl>
        <span v-else class="cost-tile__note">분해 값이 없습니다.</span>
      </div>

      <!-- AI 응대 시간 — 전화 10초 단위 × 원가 단가를 vox 전화 AI 원가(음성 AI − 웹 음성) 옆에 -->
      <div class="cost-tile">
        <span class="cost-tile__label">AI 응대 시간</span>
        <span class="cost-tile__value">{{ formatDuration(aiView.seconds) }}</span>
        <span class="cost-tile__note">{{ aiView.unitsText }}</span>
        <div v-if="aiView.expectedText" class="cost-recheck">
          <span class="cost-recheck__formula">{{ aiView.expectedText }}</span>
          <span v-if="aiView.diffLabel" class="cost-pill" :class="`is-${aiView.diffTone}`">{{ aiView.diffLabel }}</span>
        </div>
        <span v-if="aiView.phoneCostText" class="cost-tile__hint">{{ aiView.phoneCostText }}</span>
      </div>

      <!-- 10초 규칙 KPI -->
      <div class="cost-tile" :class="{ 'is-warn': ruleView.mismatched > 0 || ruleView.uncosted > 0 }">
        <span class="cost-tile__label">10초 규칙 일치</span>
        <template v-if="ruleView.measured">
          <span class="cost-tile__value">
            {{ formatNumber(ruleView.matched) }} / {{ formatNumber(ruleView.target) }}
            <small class="cost-tile__unit">일치 / 대상</small>
          </span>
          <span class="cost-tile__note">
            기타 {{ formatNumber(ruleView.other) }}(발신 기본료·통신료·전환료 — 설명되는 차이) · 불일치 {{ formatNumber(ruleView.mismatched) }}
            <template v-if="ruleView.uncosted !== null"> · 원가 없음 {{ formatNumber(ruleView.uncosted) }}</template>
          </span>
        </template>
        <template v-else>
          <span class="cost-tile__value">{{ EMPTY }}</span>
          <span class="cost-tile__note">
            운영 DB 재계산 전<template v-if="isNumber(ruleView.target)"> · 대상 {{ formatNumber(ruleView.target) }}통</template>
          </span>
        </template>
        <span class="cost-tile__hint">통화마다 ⌈AI 초 ÷ 10⌉ × 원가 단가와 vox 원가를 견줍니다.</span>
      </div>
    </div>

    <div v-for="note in warningNotes" :key="note.code" class="cost-note" role="note">
      <code class="cost-code">{{ note.code }}</code>
      <span class="cost-pill" :class="note.blocking ? 'is-danger' : 'is-muted'">{{ note.blocking ? '차단' : '비차단' }}</span>
      <span>{{ note.text }}</span>
    </div>

    <div v-if="ruleView.uncosted > 0" class="cost-mismatch">
      <div class="cost-mismatch__head">
        <span class="cost-mismatch__title">원가 없는 통화</span>
        <span class="cost-mismatch__meta">{{ uncostedMeta }}</span>
      </div>
      <ul v-if="uncostedIds.length" class="cost-ids">
        <li v-for="id in uncostedIds" :key="id"><code class="cost-id">{{ id }}</code></li>
      </ul>
      <p v-else class="cost-mismatch__empty">
        원가 없는 통화 {{ formatNumber(ruleView.uncosted) }}건의 통화 ID 가 call-api 응답에 아직 없습니다.
      </p>
    </div>

    <div v-if="ruleView.mismatched > 0" class="cost-mismatch">
      <div class="cost-mismatch__head">
        <span class="cost-mismatch__title">규칙과 다른 통화</span>
        <span class="cost-mismatch__meta">판정: 일치 / 기타(발신 통화의 차이 · 전환 통화의 초과분) / 불일치(수신 통화) — 불일치만 보입니다</span>
      </div>
      <a-table
        v-if="mismatchRows.length"
        class="cost-table"
        size="small"
        row-key="key"
        :columns="MISMATCH_COLUMNS"
        :data-source="mismatchRows"
        :pagination="false"
      >
        <template #bodyCell="{ column, record }">
          <code v-if="column.key === 'id'" class="cost-id">{{ record.id }}</code>
        </template>
      </a-table>
      <p v-else class="cost-mismatch__empty">
        불일치 {{ formatNumber(ruleView.mismatched) }}건의 통화 목록이 call-api 응답에 아직 없습니다.
      </p>
    </div>
  </section>
</template>

<script setup>
import { computed, getCurrentInstance } from 'vue';

import {
  EMPTY,
  formatDuration,
  formatNumber,
  formatUnitPrice,
  formatWon,
  isNumber,
} from '../../service/settlementFormat';
import { WARNING_META, aiTimeCalls, unitsMinutesText, warningText } from '../../service/settlementRules';

const props = defineProps({
  /** 정산 상세(요청 계약 2) — 부모(ReconTab)가 셸에서 받은 이 병원·달 상세 그대로 */
  detail: { type: Object, required: true },
});

/** 화면 명세 2 대사 탭 — 고정 경고 박스 문구 */
const CSV_DURATION_WARNING =
  'vox 대시보드 CSV 의 통화시간 = AI 구간 + 상담사 연결 구간 × 2 입니다. AI 시간이나 과금 시간으로 쓰지 마세요.';

/** 이 칸들에 걸린 경고 — 순서대로 코드·차단 여부·설명을 붙인다 */
const PANEL_WARNINGS = [
  {
    code: 'WEB_VOICE_COST_MISMATCH',
    text: (w) => `${warningText(w)}. ${WARNING_META.WEB_VOICE_COST_MISMATCH.description}`,
  },
  {
    code: 'UNIT_RULE_MISMATCH',
    // 요금표 v70 — 청구는 수신·발신 통화 단위(통화마다 AI + 상담사 통화를 더해 10초 올림)로 한다(개정 4·5)
    text: () =>
      `${WARNING_META.UNIT_RULE_MISMATCH.description} 서티 청구는 우리 통화 시간(수신·발신 통화마다 AI + 상담사 통화를 10초로 올린 단위)으로 계산합니다.`,
  },
  {
    code: 'UNIT_RULE_UNCOSTED',
    // 해결 안내가 세 가지(미러에 원가 없음 · 원가 적게·마진 많게 · 마감이 vox 에서 다시 읽음)를 다 담는다
    text: () =>
      WARNING_META.UNIT_RULE_UNCOSTED.resolutions.find((r) => r.hint)?.hint ?? WARNING_META.UNIT_RULE_UNCOSTED.description,
  },
];

/** cost.split 의 키 — vox 직접 두 키는 계약 추가 제안이라 있을 때만 줄을 만든다 */
const SPLIT_ROWS = [
  { key: 'voice_ai', label: '음성 AI', tip: 'AI 음성 응대 원가' },
  { key: 'web_text', label: '웹 텍스트', tip: '웹 테스트 텍스트 메시지 원가(메시지당)' },
  { key: 'vox_direct_transfer', label: 'vox 직접 전환료', tip: 'vox 직접 연결의 전환 원가' },
  { key: 'vox_direct_telecom', label: 'vox 직접 통신료', tip: 'vox 직접 연결의 통신료 원가' },
  {
    key: 'other',
    label: '기타',
    tip: '위 항목으로 나뉘지 않은 vox 원가(전환료, leg 기록이 없는 cold 전환, 부가 항목 등) — 청구하지 않습니다',
  },
];

const MISMATCH_COLUMNS = [
  { title: '통화 ID', key: 'id', dataIndex: 'id' },
  { title: 'AI 초', key: 'aiSeconds', dataIndex: 'aiSeconds', align: 'right' },
  { title: '기대 원가', key: 'expected', dataIndex: 'expected', align: 'right' },
  { title: 'vox 원가', key: 'vox', dataIndex: 'vox', align: 'right' },
  { title: '차이', key: 'diff', dataIndex: 'diff', align: 'right' },
];

const titleId = `st-recon-cost-${getCurrentInstance()?.uid ?? 0}`;

const num = (v) => (isNumber(v) ? v : null);
const round2 = (n) => Math.round(n * 100) / 100;
const signedWon = (n) => (isNumber(n) && n > 0 ? `+${formatWon(n)}` : formatWon(n));

const cost = computed(() => props.detail?.cost ?? {});
const voxCall = computed(() => num(cost.value.vox_call));

const splitRows = computed(() => {
  const split = cost.value.split;
  if (!split || typeof split !== 'object') return [];
  return SPLIT_ROWS.filter((row) => isNumber(split[row.key])).map((row) => ({ ...row, value: split[row.key] }));
});

const aiView = computed(() => {
  const ai = props.detail?.usage?.ai ?? {};
  const seconds = num(ai.seconds);
  const units = num(ai.units_10s);
  const rateAi = props.detail?.rates?.ai ?? null;
  const unitSec = num(rateAi?.unit_sec) ?? 10;
  const costPerUnit = num(rateAi?.cost_per_unit);
  // AI 시간·단위와 같은 범위(수신 + 발신)의 통화 수 — ai_answered(수신만)를 옆에 두지 않는다
  const calls = aiTimeCalls(props.detail);

  // The unit count means nothing to the reader — show it as time ('68분 30초'), never as 'n단위'
  const unitsTime = units === null ? '' : unitSec === 10 ? unitsMinutesText(units) : formatDuration(units * unitSec);
  const unitsParts = [];
  if (units !== null) unitsParts.push(`통화마다 ${unitSec}초로 올린 시간 ${unitsTime}`);
  if (calls.count !== null) unitsParts.push(`AI 가 받은 통화 ${formatNumber(calls.count)}통(${calls.scope})`);

  // 기본 요율이 없는 달(rates null)은 원가 단가를 몰라 견주지 않는다
  let expectedText = '';
  let diffLabel = '';
  let diffTone = 'muted';
  let phoneCostText = '';
  if (units !== null && costPerUnit !== null) {
    const expected = round2(units * costPerUnit);
    expectedText = `${unitsTime} × ${unitSec}초당 ${formatUnitPrice(costPerUnit)}원 = ${formatWon(expected)}`;
    const voice = num(cost.value.split?.voice_ai);
    if (voice !== null) {
      // 음성 AI 원가에는 웹 음성 원가가 들어 있다 — 전화 단위끼리 견주도록 뺀다(웹 음성이 없는 옛 응답은 0)
      const webVoice = num(props.detail?.usage?.web_voice?.vox_cost) ?? 0;
      const phone = round2(voice - webVoice);
      const diff = round2(phone - expected);
      diffLabel = diff === 0 ? 'vox 전화 AI 원가와 같음' : `vox 전화 AI 원가와 차이 ${signedWon(diff)}`;
      diffTone = diff === 0 ? 'ok' : 'muted';
      phoneCostText =
        webVoice > 0
          ? `vox 전화 AI 원가 ${formatWon(phone)} = 음성 AI ${formatWon(voice)} − 웹 음성 ${formatWon(webVoice)}`
          : `vox 전화 AI 원가 ${formatWon(phone)}(웹 음성 원가 없음)`;
    }
  }
  return {
    seconds,
    unitsText: unitsParts.join(' · ') || '올림한 시간이 없습니다.',
    expectedText,
    diffLabel,
    diffTone,
    phoneCostText,
  };
});

const ruleView = computed(() => {
  const rule = cost.value.unit_rule ?? null;
  const matched = num(rule?.matched);
  return {
    measured: matched !== null,
    target: num(rule?.target),
    matched,
    other: num(rule?.other) ?? 0,
    mismatched: num(rule?.mismatched) ?? 0,
    // 원가 없는 통화 — 일치 + 기타 + 불일치 + 원가 없음 = 대상. 없는 옛 응답이면 null(보이지 않는다)
    uncosted: num(rule?.uncosted),
  };
});

/** 원가 없는 통화 ID(최대 20건 — call-api 가 자른다) */
const uncostedIds = computed(() => {
  const list = cost.value.unit_rule?.uncosted_call_ids;
  return Array.isArray(list) ? list.filter((id) => typeof id === 'string' && id) : [];
});

const uncostedMeta = computed(() => {
  const n = ruleView.value.uncosted ?? 0;
  const shown = uncostedIds.value.length;
  const head = 'AI 가 받았는데 vox 원가가 미러에 없는 통화 — 원가·마진 계산에서 빠집니다';
  return shown && shown < n ? `${head} · ${formatNumber(n)}건 중 ${formatNumber(shown)}건만 보입니다` : head;
});

const mismatchRows = computed(() => {
  const list = cost.value.unit_rule?.mismatches;
  if (!Array.isArray(list)) return [];
  return list.map((m, i) => ({
    key: `${m?.vox_call_id ?? 'row'}:${i}`,
    id: m?.vox_call_id ?? EMPTY,
    aiSeconds: isNumber(m?.ai_seconds) ? `${formatNumber(m.ai_seconds)}초` : EMPTY,
    expected: formatWon(m?.expected_cost),
    vox: formatWon(m?.vox_cost),
    diff: signedWon(m?.diff),
  }));
});

/** 이 칸들에 걸린 경고(있는 것만) — 차단 여부는 경고 객체의 blocking */
const warningNotes = computed(() => {
  const list = Array.isArray(props.detail?.warnings) ? props.detail.warnings : [];
  return PANEL_WARNINGS.flatMap(({ code, text }) => {
    const w = list.find((item) => item?.code === code);
    return w ? [{ code, blocking: w.blocking === true, text: text(w) }] : [];
  });
});
</script>

<style scoped lang="scss">
@use '../../styles/settlement' as *;

.cost-card {
  @include st-card;
  display: flex;
  flex-direction: column;
  gap: 12px;

  &__head {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 6px 10px;
  }

  &__title {
    margin: 0;
    font-size: 15px;
    font-weight: 700;
    color: $st-text;
  }

  &__meta {
    font-size: 12px;
    color: $st-muted;
  }
}

.cost-alert {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px 12px;
  border: 1px solid $st-danger-border;
  border-radius: 10px;
  background: $st-danger-bg;
  color: $st-danger;
  font-size: 13px;
  font-weight: 600;

  &__icon {
    margin-top: 3px;
  }
}

.cost-tiles {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 240px), 1fr));
  gap: 12px;
}

.cost-tile {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px 14px;
  border: 1px solid $st-border;
  border-radius: 10px;
  background: $st-bg;

  &.is-warn {
    border-color: $st-warning-border;
    background: $st-warning-bg;
  }

  &__label {
    font-size: 12px;
    font-weight: 600;
    color: $st-text-secondary;
  }

  &__value {
    @include st-num;
    font-size: 20px;
    font-weight: 700;
    color: $st-text;
    line-height: 1.35;
  }

  &__unit {
    margin-left: 4px;
    font-size: 12px;
    font-weight: 500;
    color: $st-muted;
  }

  &__note {
    font-size: 12px;
    color: $st-text-secondary;
  }

  &__hint {
    font-size: 11.5px;
    color: $st-muted;
  }
}

.cost-split {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin: 4px 0 0;
  padding-top: 6px;
  border-top: 1px solid $st-border-strong;

  &__row {
    display: flex;
    justify-content: space-between;
    gap: 8px;
    font-size: 12.5px;
    cursor: help;

    dt {
      font-weight: 400;
      color: $st-text-secondary;
    }

    dd {
      @include st-num;
      margin: 0;
      color: $st-text;
    }
  }
}

.cost-recheck {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 8px;
  margin-top: 4px;
  padding-top: 6px;
  border-top: 1px solid $st-border-strong;
  font-size: 12.5px;
  color: $st-text-secondary;

  &__formula {
    @include st-num;
  }
}

.cost-pill {
  @include st-pill;

  &.is-ok {
    @include st-pill-tone($st-ok, $st-ok-bg, $st-ok-border);
  }

  &.is-danger {
    @include st-pill-tone($st-danger, $st-danger-bg, $st-danger-border);
  }
}

.cost-ids {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 12px;
  margin: 0;
  padding: 8px 12px;
  border-radius: 8px;
  background: $st-bg;
  list-style: none;
}

.cost-note {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px 8px;
  padding: 8px 12px;
  border-radius: 8px;
  background: $st-bg;
  font-size: 12.5px;
  color: $st-text-secondary;
}

.cost-code {
  padding: 0 6px;
  border-radius: 4px;
  background: #f5f5f5;
  color: $st-text-secondary;
  font-size: 11.5px;
}

.cost-mismatch {
  display: flex;
  flex-direction: column;
  gap: 8px;

  &__head {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 4px 10px;
  }

  &__title {
    font-size: 13px;
    font-weight: 600;
    color: $st-text;
  }

  &__meta {
    font-size: 12px;
    color: $st-muted;
  }

  &__empty {
    margin: 0;
    padding: 10px 12px;
    border-radius: 8px;
    background: $st-bg;
    font-size: 12.5px;
    color: $st-muted;
  }
}

.cost-table {
  :deep(.ant-table) {
    font-size: 12.5px;
  }

  :deep(.ant-table-cell) {
    @include st-num;
  }
}

.cost-id {
  color: $st-text-secondary;
  font-size: 12px;
  white-space: nowrap;
}
</style>
